import { formatMoney } from "@bw/shared/utils";
import type { CartQuoteResponse } from "@bw/shared/types/generated";
import type { CartSlot } from "../state/cartStore";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * The BINDING quote breakdown — the ONLY numbers allowed on the review screen.
 *
 * Renders each quote line (per-slot price from the backend's ReservationQuote)
 * plus the subtotal + total + currency from CartQuoteResponse. The 3a per-cell
 * client estimate is NOT shown here — there is no client-side pricing on this
 * screen. Court names are looked up from the cart for a friendly label, but the
 * PRICE is always the server's line total, never derived.
 *
 * When the chosen group size exceeds a court's included player count, the
 * backend's quote carries an `extra_player` surcharge line item on that court's
 * `line_items` (slice ② allocation). We surface it — label + server-computed
 * amount, VERBATIM — beneath the court so the host sees why the price went up.
 * It is shown ONLY when the backend includes it (count > included); it is never
 * client-computed.
 */

/** The surcharge line item codes worth surfacing beneath a court's price. */
const EXTRA_PLAYER_CODE = "extra_player";

/** Venue-local "Tue, Jun 10 · 9:00 AM" for a quote line, best-effort. */
function lineLabel(startTime: string, courtName: string): string {
  let when = startTime;
  try {
    when = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(startTime));
  } catch {
    when = startTime;
  }
  return `${courtName} · ${when}`;
}

export function QuoteSummary({
  quote,
  slots,
}: {
  quote: CartQuoteResponse;
  slots: CartSlot[];
}) {
  const currency = quote.currency ?? "USD";
  const items = quote.items ?? [];

  function courtName(courtId: string, startTime: string): string {
    const slot = slots.find(
      (s) => s.court_id === courtId && s.start_time === startTime,
    );
    return slot?.court_name ?? "Court";
  }

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-label="Price breakdown"
      sx={{ overflow: "hidden" }}
    >
      <Typography
        component="h2"
        variant="h6"
        sx={{ px: 2, pt: 2, fontWeight: 800 }}
      >
        Your courts
      </Typography>
      <List disablePadding>
        {items.map((line) => {
          const lineTotal = line.quote?.total;
          const lineCurrency = line.quote?.currency ?? currency;
          // The server's extra-player surcharge line(s) for this court, surfaced
          // verbatim. Present only when the group exceeds the included count.
          const surcharges = (line.quote?.line_items ?? []).filter(
            (li) => li.code === EXTRA_PLAYER_CODE,
          );
          return (
            <ListItem
              key={`${line.court_id}|${line.start_time}`}
              divider
              sx={{ px: 2, py: 1.5, display: "block" }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography variant="body2">
                  {lineLabel(
                    line.start_time,
                    courtName(line.court_id, line.start_time),
                  )}
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                >
                  {formatMoney(lineTotal, lineCurrency)}
                </Typography>
              </Stack>
              {surcharges.length > 0 ? (
                <List disablePadding sx={{ mt: 0.5 }}>
                  {surcharges.map((li, i) => (
                    <ListItem
                      key={`${li.code}-${i}`}
                      disableGutters
                      sx={{
                        py: 0.25,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {li.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        +{formatMoney(li.total, lineCurrency)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              ) : null}
            </ListItem>
          );
        })}
      </List>

      <Box
        component="dl"
        sx={{ m: 0, px: 2, py: 1.5, bgcolor: "action.hover" }}
      >
        {/* Only show a separate subtotal when it differs from the total (e.g. a
            fee/discount applies) — otherwise it's redundant with the total. */}
        {quote.campaign || String(quote.subtotal) !== String(quote.total) ? (
          <Stack
            component="div"
            direction="row"
            sx={{ justifyContent: "space-between" }}
          >
            <Typography component="dt" color="text.secondary">
              Subtotal
            </Typography>
            <Typography component="dd" sx={{ m: 0 }}>
              {formatMoney(quote.subtotal, currency)}
            </Typography>
          </Stack>
        ) : null}
        {quote.campaign ? (
          <Stack
            component="div"
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-between", mt: 0.5 }}
          >
            <Typography component="dt" color="success.main">
              {quote.campaign.name}
            </Typography>
            <Typography
              component="dd"
              color="success.main"
              sx={{ m: 0, fontVariantNumeric: "tabular-nums" }}
            >
              −{formatMoney(quote.campaign.discount, currency)}
            </Typography>
          </Stack>
        ) : null}
        <Stack
          component="div"
          direction="row"
          sx={{ justifyContent: "space-between", mt: 0.5 }}
        >
          <Typography component="dt" sx={{ fontWeight: 800 }}>
            Total
          </Typography>
          <Typography
            component="dd"
            sx={{ m: 0, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
          >
            {formatMoney(quote.total, currency)}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
}
